'use client'

import { Eye } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { cn } from '@/lib/utils'

interface ClickableImageProps {
  src: string
  alt: string
  onClick: () => void
  showOverlay?: boolean
  className?: string
  children?: React.ReactNode
  width?: number
  height?: number
  fill?: boolean
  priority?: boolean
  placeholder?: 'blur' | 'empty'
  blurDataURL?: string
  sizes?: string
  style?: React.CSSProperties
  quality?: number
  hoverScale?: boolean
}

export function ClickableImage({
  src,
  alt,
  onClick,
  showOverlay = true,
  className,
  children,
  fill,
  width,
  height,
  hoverScale = false,
  ...imageProps
}: ClickableImageProps) {
  const [isHovered, setIsHovered] = React.useState(false)

  const containerClass = fill
    ? cn(
        'relative w-full h-full cursor-pointer group transition-transform duration-200',
        hoverScale && 'hover:scale-[1.02]',
        className
      )
    : cn(
        'relative cursor-pointer group transition-transform duration-200',
        hoverScale && 'hover:scale-[1.02]',
        className
      )

  return (
    <div
      className={containerClass}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      aria-label={`Open image preview: ${alt}`}
      style={fill ? undefined : { width: width, height: height }}
    >
      <Image
        src={src}
        alt={alt}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        fill={fill}
        className={cn(
          'transition-all duration-200',
          showOverlay && 'group-hover:brightness-75',
          fill ? 'object-cover' : ''
        )}
        {...imageProps}
        // User content (diagrams, screenshots) can exceed Next.js's 9000px optimizer limit.
        unoptimized
      />

      {/* Overlay with preview icon */}
      {showOverlay && (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-200',
            isHovered ? 'opacity-100' : 'opacity-0'
          )}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg transform transition-transform duration-200 group-hover:scale-110">
            <Eye className="h-6 w-6 text-gray-800" />
          </div>
        </div>
      )}

      {children}
    </div>
  )
}
